import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type { ApiRole, UiRole } from '../schemas/roles.schema';

import {
  type CheckRevokeRoleAffectedUsersArgs,
  type DeleteRoleByIdArgs,
  type FetchRoleByIdArgs,
  type EditRoleByIdArgs,
  type CreateRoleArgs,
  type FetchRolesArgs,
} from './types/roles.types';
import { mapRoleFromApi, mapRoleToApi } from './mappers/roles.mappers';
import { destroy, patch, post, get } from './axios';

export const fetchRoles = async ({
  filters,
  organizationId,
}: FetchRolesArgs) => {
  const response = await get<BaseApiPaginatedResponse<ApiRole[]>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}roles`,
    {
      query: filters,
    },
  );
  return {
    ...response,
    data: response?.data?.map(mapRoleFromApi) as UiRole[],
  };
};

export const deleteRoleById = async ({
  id,
  organizationId,
}: DeleteRoleByIdArgs) => {
  return destroy(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}roles/${id}`,
  );
};

export const createRole = async ({
  organizationId,
  permissionKeys,
  translations,
  users,
}: CreateRoleArgs) => {
  const response = await post<ApiRole>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}roles`,
    mapRoleToApi({
      permissionKeys,
      translations,
      users,
    }),
  );
  if (!response?.data) return null;
  return mapRoleFromApi(response.data);
};

export const checkRevokeRoleAffectedUsers = async ({
  id,
  organizationId,
}: CheckRevokeRoleAffectedUsersArgs) => {
  const response = await get<BaseApiResponse<{ totalAssignedUsers: number }>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}roles/${id}/check`,
  );
  return response?.data?.totalAssignedUsers || 0;
};

export const fetchRoleById = async ({
  id,
  organizationId,
}: FetchRoleByIdArgs) => {
  const response = await get<BaseApiResponse<ApiRole>>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}roles/${id}`,
  );
  if (!response?.data) return null;
  return mapRoleFromApi(response.data);
};

export const editRoleById = async ({
  id,
  organizationId,
  permissionKeys,
  translations,
  users,
}: EditRoleByIdArgs) => {
  const response = await patch<ApiRole>(
    `/v2/${organizationId ? `organizations/${organizationId}/` : ''}roles/${id}`,
    mapRoleToApi({
      permissionKeys,
      translations,
      users,
    }),
  );
  if (!response?.data) return null;
  return mapRoleFromApi(response.data);
};
